import { useMsal } from '@azure/msal-react';
import { useImportOutlookMeetingsMutation } from '@consigli/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getMutationErrorMessage } from '../utils';

import type { OutlookImportSelection } from '../outlook-import/outlook-import.types';

interface UseOutlookImportParams {
  projectId: number;
  onImportStart?: () => void;
}

export const useOutlookImport = ({ projectId, onImportStart }: UseOutlookImportParams) => {
  const { t } = useTranslation();
  const { accounts, instance } = useMsal();
  const [importOutlookMeetings, { isLoading: isImporting }] = useImportOutlookMeetingsMutation();
  const [isOutlookImporting, setIsOutlookImporting] = useState(false);

  const importOutlook = async (selection: OutlookImportSelection) => {
    if (selection.eventIds.length === 0 && selection.seriesIds.length === 0) {
      return;
    }

    const account = accounts[0];
    if (!account) {
      toast.error(t('meeting-secretary.outlook-import.import-error'));
      return;
    }

    onImportStart?.();
    setIsOutlookImporting(true);

    try {
      const tokenRequest = {
        scopes: ['Calendars.Read', 'User.Read'],
        account,
      };
      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);

      const result = await importOutlookMeetings({
        projectId,
        msAccessToken: tokenResponse.accessToken,
        userEmail: account.username || '',
        userId: account.localAccountId || '',
        eventIds: selection.eventIds,
        seriesIds: selection.seriesIds,
      }).unwrap();

      const reason = Array.from(
        new Set(result.errors.map((e: { error?: string }) => e.error).filter(Boolean)),
      ).join(' ');
      if (result.importedMeetingCount === 0 && result.errors.length > 0) {
        toast.error(
          reason
            ? t('meeting-secretary.outlook-import.import-failed', { reason })
            : t('meeting-secretary.outlook-import.import-error'),
        );
      } else if (result.errors.length > 0) {
        toast.warn(
          t('meeting-secretary.outlook-import.import-partial', {
            imported: result.importedMeetingCount,
            failed: result.errors.length,
            reason,
          }),
        );
      } else {
        toast.success(
          t('meeting-secretary.outlook-import.import-success', {
            count: result.importedMeetingCount,
          }),
        );
      }
    } catch (error) {
      console.error('Error importing Outlook meetings:', error);
      toast.error(
        getMutationErrorMessage(error, t('meeting-secretary.outlook-import.import-error')),
      );
    } finally {
      setIsOutlookImporting(false);
    }
  };

  return { importOutlook, isOutlookImporting, isImporting };
};
